import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { queryKeys } from '@/lib/query-keys';
import { useSession } from '@/lib/session';
import { apiClient } from '@borradh-workspace/api-client';
import type { PlatformAccessLogResponse } from '@borradh-workspace/api-client/types';
import { platformAccessReasonLabels } from '@borradh-workspace/labels';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

export function PlatformAccessLog() {
  const { data: session } = useSession();
  const organizationId = session?.user?.organizationId;
  const canView =
    session?.user?.orgRole === 'owner' || session?.user?.orgRole === 'admin';
  const [page, setPage] = useState<{
    organizationId: string | null | undefined;
    offset: number;
  }>({ organizationId, offset: 0 });
  const offset = page.organizationId === organizationId ? page.offset : 0;
  const setOffset = (update: (value: number) => number) =>
    setPage({ organizationId, offset: update(offset) });
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.privacy.platformAccessLog(organizationId, offset),
    queryFn: () =>
      apiClient.get<PlatformAccessLogResponse>(
        `organizations/${organizationId}/access-log`,
        { searchParams: { offset } }
      ),
    enabled: Boolean(organizationId && canView),
  });
  if (!canView) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Borradh access log</CardTitle>
        <CardDescription>
          Support access requests recorded from the release of this log. A
          request may be refused; earlier access may not appear.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isPending ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading access log...
          </p>
        ) : isError ? (
          <div role="alert" className="space-y-2">
            <p>We could not load the access log.</p>
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        ) : !data?.items.length ? (
          <p className="text-sm text-muted-foreground">
            No Borradh access recorded.
          </p>
        ) : (
          <ul className="divide-y">
            {data.items.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-col gap-1 py-3 sm:flex-row sm:justify-between sm:gap-4"
              >
                <div>
                  <p className="text-sm font-medium">
                    {entry.reason
                      ? platformAccessReasonLabels[entry.reason]
                      : 'Borradh support'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {entry.accessMode === 'impersonation'
                      ? 'User session requested'
                      : 'Workspace view requested'}
                  </p>
                </div>
                <time
                  className="text-sm text-muted-foreground"
                  dateTime={entry.createdAt}
                >
                  {new Date(entry.createdAt).toLocaleString()}
                </time>
              </li>
            ))}
          </ul>
        )}
        {(offset > 0 || data?.hasMore) && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={offset === 0 || isPending}
              onClick={() => setOffset((value) => Math.max(0, value - 25))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={!data?.hasMore || isPending}
              onClick={() => setOffset((value) => value + 25)}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
